import React, { memo } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import * as Flags from "country-flag-icons/string/3x2";
import geoUrl from "./cod/files/mapNoAntarctica.json"

// Visited countries mapped to ISO 3166-1 alpha-2 codes used by country-flag-icons.
// Names must match the `properties.name` field in mapNoAntarctica.json.
export const VISITED_COUNTRIES = [
  { name: "Peru", code: "PE" },
  { name: "United States of America", code: "US" },
  { name: "England", code: "GB" },
  { name: "Belize", code: "BZ" },
  { name: "Iceland", code: "IS" },
  { name: "China", code: "CN" },
  { name: "Italy", code: "IT" },
  { name: "Germany", code: "DE" },
  { name: "France", code: "FR" },
  { name: "United Kingdom", code: "GB" },
  { name: "Canada", code: "CA" },
  { name: "Netherlands", code: "NL" },
  { name: "Mexico", code: "MX" },
  { name: "Japan", code: "JP" },
  { name: "Denmark", code: "DK" },
  { name: "Dominican Republic", code: "DO" },
  { name: "Austria", code: "AT" },
  { name: "Uganda", code: "UG" },
  { name: "United Arab Emirates", code: "AE" },
  { name: "Costa Rica", code: "CR" },
  { name: "Switzerland", code: "CH" },
  { name: "Taiwan", code: "TW" },
  { name: "Spain", code: "ES" },
  { name: "Norway", code: "NO" },
];

const VISITED_NAMES = new Set(VISITED_COUNTRIES.map(c => c.name));
const CODE_BY_NAME = VISITED_COUNTRIES.reduce((acc, c) => {
  acc[c.name] = c.code;
  return acc;
}, {});

// Encode the imported SVG string as a data URL so we can drop it into an <image>.
const flagDataUrl = (code) => {
  const svg = Flags[code];
  if (!svg) return null;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const FLAG_WIDTH = 18;
const FLAG_HEIGHT = 12;

const MapChart = ({
  setTooltipContent,
  mapSelection,
  setMapSelection,
  toggleSwitch,
  onCountrySelect,
  isInactive,
}) => {

  return (
    <>
      <ComposableMap data-tip="" projectionConfig={{ scale: 200 }}>
        <Geographies geography={geoUrl}>
          {({ geographies }) => (
            <>
              {geographies.map(geo => {
                const isVisited = VISITED_NAMES.has(geo.properties.name);
                const selectCountry = () => {
                  if (!isVisited) return;

                  const name = geo.properties.name;
                  setMapSelection(name);
                  onCountrySelect({
                    name,
                    code: CODE_BY_NAME[name],
                  });
                };

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    role={isVisited ? "button" : undefined}
                    tabIndex={isVisited && !isInactive ? 0 : -1}
                    aria-label={isVisited ? `View photos from ${geo.properties.name}` : undefined}
                    onMouseEnter={() => {
                      const { name } = geo.properties;
                      setTooltipContent({
                        content: `${name}`
                      });
                    }}
                    onMouseLeave={() => {
                      setTooltipContent({ content: "" });
                    }}
                    onClick={selectCountry}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        selectCountry();
                      }
                    }}
                    style={
                      geo.properties.name === mapSelection
                        ? {
                          default: {
                            fill: "#000",
                            outline: "none",
                          },
                          hover: {
                            fill: "#00000090",
                            outline: "none"
                          },
                          pressed: {
                            fill: "#000",
                            outline: "none"
                          }
                        } : toggleSwitch && isVisited
                          ? {
                            default: {
                              fill: "#e8e8e8",
                              stroke: "#9a9a9a70",
                              strokeWidth: 0.6,
                              outline: "none",
                              cursor: "pointer",
                              transition: "0.4s",
                            },
                            hover: {
                              fill: "#dcdcdc",
                              stroke: "#9a9a9a",
                              strokeWidth: 0.4,
                              outline: "none",
                              cursor: "pointer",
                              transition: "0.4s",
                            },
                            pressed: {
                              fill: "#cfcfcf",
                              outline: "none",
                              transition: "0.4s",
                            }
                          }
                          : {
                            default: {
                              fill: "#e8e8e8",
                              outline: "none",
                              transition: "0.4s",
                            },
                            hover: {
                              fill: "#00000040",
                              outline: "none",
                              transition: "0.4s",
                            },
                            pressed: {
                              fill: "#000",
                              outline: "none",
                              transition: "0.4s",
                            }
                          }
                    }
                  />
                );
              })}

              {toggleSwitch && !isInactive && geographies
                .filter(geo => VISITED_NAMES.has(geo.properties.name))
                .map(geo => {
                  const code = CODE_BY_NAME[geo.properties.name];
                  const url = flagDataUrl(code);
                  if (!url) return null;
                  const centroid = geoCentroid(geo);
                  if (!centroid || Number.isNaN(centroid[0]) || Number.isNaN(centroid[1])) {
                    return null;
                  }
                  return (
                    <Marker
                      key={`flag-${geo.rsmKey}`}
                      coordinates={centroid}
                    >
                      <g style={{ filter: "drop-shadow(0 1px 1.5px rgba(0,0,0,0.35))" }}>
                        <image
                          href={url}
                          xlinkHref={url}
                          width={FLAG_WIDTH}
                          height={FLAG_HEIGHT}
                          x={-FLAG_WIDTH / 2}
                          y={-FLAG_HEIGHT / 2}
                          style={{ pointerEvents: "none" }}
                        />
                      </g>
                    </Marker>
                  );
                })}
            </>
          )}
        </Geographies>
      </ComposableMap>
    </>
  );
};

export default memo(MapChart);
